import React from 'react';
import Markdown from 'markdown-to-jsx';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { decodeDisplayText } from '../../util/displayText';
import { createMarkdownToJsxOptions, normalizeMarkdownForStorage, shouldRenderMarkdownAsPlainText } from '../../util/markdownTheme';
import { tokenizeInteractiveText } from '../../util/textPatterns';
import { hasMarkdownFormatting, linkifyMarkdownTitle, getValidMarkdownAnchorProps, normalizeMarkdownLinkHref } from '../../util/titleText';
import { AuthContext } from '../../util/context';
import { openExternalUrl } from '../../util/downloadHelpers';
import SocialNetworkModal from '../Messages/SocialNetworkModal';
import StockTickerModal from '../Messages/StockTickerModal';
import TitleLinkMenu from './TitleLinkMenu';
import ExpandablePreviewText from './ExpandablePreviewText';

const Content = styled.div`
    white-space: pre-wrap;
    p, li { white-space: pre-wrap; }
    > :first-child { margin-top: 0 !important; }
    > :last-child { margin-bottom: 0 !important; }
`;

const tokenHref = token => {
    switch (token.type) {
        case 'url': return normalizeMarkdownLinkHref(token.text);
        case 'markdownLink': return token.url;
        case 'mention': return `mention:${encodeURIComponent(token.text.slice(1))}`;
        case 'hashtag': return `hashtag:${encodeURIComponent(token.text.slice(1))}`;
        case 'stockTicker': return `stock:${encodeURIComponent(token.text)}`;
        case 'email': return `copy-email:${encodeURIComponent(token.text)}`;
        case 'phone': return `copy-phone:${encodeURIComponent(token.text)}`;
        default: return null;
    }
};

const TitleActionsContext = React.createContext({});

// Keep this component type stable across form validation and blur updates.
function InteractiveTitleAnchor(props) {
    const actions = React.useContext(TitleActionsContext);
    return <TitleAction {...props} {...actions} />;
}

function TitleAction({ href = '', children, onMention, onHashtag, onStock, onEdit, ...props }) {
    const { t } = useTranslation();
    const [copied, setCopied] = React.useState(false);
    const [linkMenu, setLinkMenu] = React.useState(null);
    const closeLinkMenu = React.useCallback(() => setLinkMenu(null), []);
    const timer = React.useRef(null);
    const showCopied = () => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
    };
    React.useEffect(() => () => clearTimeout(timer.current), []);
    const action = /^(mention|hashtag|stock|copy-email|copy-phone):/.exec(href)?.[1];
    const color = action === 'mention' ? '#1da1f2' : action === 'hashtag' ? '#ff7a00' : action === 'stock' ? '#00873c' : action ? '#2b88d9' : '#5ca2ff';
    const onClick = async event => {
        event.preventDefault();
        event.stopPropagation();
        if (!action) {
            const url = normalizeMarkdownLinkHref(href);
            if (/^https?:\/\//i.test(url)) {
                if (event.type === 'click' && (event.metaKey || event.ctrlKey)) {
                    closeLinkMenu();
                    openExternalUrl(url);
                } else {
                    setLinkMenu({ href: url, anchor: event.currentTarget });
                }
            }
            return;
        }
        let value;
        try { value = decodeURIComponent(href.slice(action.length + 1)); }
        catch { return; }
        if (action === 'mention') onMention(value);
        else if (action === 'hashtag') onHashtag(`#${value}`);
        else if (action === 'stock') onStock(value);
        else {
            try {
                await navigator.clipboard.writeText(action === 'copy-phone' ? value.replace(/\s/g, '') : value);
                showCopied();
            } catch { /* Leave the text available for manual selection if copying fails. */ }
        }
    };
    const shared = {
        ...getValidMarkdownAnchorProps(props),
        'data-message-interactive': 'true',
        onClick,
        onContextMenu: action ? undefined : onClick,
        onKeyDown: action ? undefined : event => {
            if (event.key === ' ' || event.key === 'ArrowDown') onClick(event);
        },
        'aria-haspopup': action ? undefined : 'menu',
        'aria-expanded': action ? undefined : Boolean(linkMenu),
        style: { color, cursor: 'pointer', font: 'inherit', fontWeight: 600, padding: 0, border: 0, background: 'none', textDecoration: action ? 'none' : 'underline', overflowWrap: 'anywhere', textAlign: 'inherit' }
    };
    if (!action && /^[a-z][a-z0-9+.-]*:/i.test(href) && !/^https?:/i.test(href)) return <span>{children}</span>;
    return <>
        {action ? <button {...shared} type="button">{children}</button> : <a {...shared} href={href} target="_blank" rel="noopener noreferrer">{children}</a>}
        {linkMenu ? <TitleLinkMenu link={linkMenu} onClose={closeLinkMenu} onEdit={onEdit} onCopied={showCopied} /> : null}
        {copied ? <span role="status" style={{ fontSize: 12, marginLeft: 6 }}>{t('messages:copied')}</span> : null}
    </>;
}

export default function TitleContent({ title = '', placeholder = '', readOnly = false, onTaskCheckboxToggle, onEdit, messageId }) {
    const auth = React.useContext(AuthContext);
    const [username, setUsername] = React.useState(null);
    const [stock, setStock] = React.useState(null);
    React.useEffect(() => {
        setUsername(null);
        setStock(null);
    }, [title]);
    const normalized = normalizeMarkdownForStorage(title);
    const onHashtag = React.useCallback(tag => {
        auth?.context?.setSearchTerm?.(tag);
        auth?.context?.setSearchActive?.(true);
    }, [auth]);
    const options = React.useMemo(() => {
        const base = createMarkdownToJsxOptions({
            surface: 'dark',
            bodyFontSizePx: 17,
            onTaskCheckboxToggle: readOnly ? undefined : onTaskCheckboxToggle
        });
        return { ...base, overrides: { ...base.overrides, a: { component: InteractiveTitleAnchor } } };
    }, [readOnly, onTaskCheckboxToggle]);

    if (!title) return placeholder;
    const plainText = shouldRenderMarkdownAsPlainText(normalized);
    const markdown = !plainText && hasMarkdownFormatting(normalized);

    return <>
        <TitleActionsContext.Provider value={{ onMention: setUsername, onHashtag, onStock: setStock, onEdit: readOnly ? undefined : onEdit }}>
        <ExpandablePreviewText lines={4} lineHeight={24} contentKey={title} resetKey={messageId || title}>
        <Content>
            {markdown ? <Markdown options={options}>{linkifyMarkdownTitle(normalized)}</Markdown> : plainText ? decodeDisplayText(title) : tokenizeInteractiveText(title).map((token, index) => {
                const href = tokenHref(token);
                return href ? <InteractiveTitleAnchor key={index} href={href}>{decodeDisplayText(token.text)}</InteractiveTitleAnchor> : <React.Fragment key={index}>{decodeDisplayText(token.text)}</React.Fragment>;
            })}
        </Content>
        </ExpandablePreviewText>
        </TitleActionsContext.Provider>
        <SocialNetworkModal isOpen={username !== null} username={username || ''} onClose={() => setUsername(null)} />
        <StockTickerModal isOpen={stock !== null} symbol={stock || ''} onClose={() => setStock(null)} onSelectPlatform={url => {
            setStock(null);
            openExternalUrl(url);
        }} />
    </>;
}
